'use client'

import { useEffect, useState } from 'react'
import { ArrowLeft, Play, Crown, TrendingDown } from 'lucide-react'
import { getDetailExperimentForFunnel } from '../../../services/experiments/experimentsService'
import { DetailLoadingSkeleton } from './TableLoadingSkeleton'
import { dictionary } from '@/utils/dictionary'
import { formatCurrency, getCurrencySymbol } from '@/utils/formatters'
import { useAuth } from '@/context/AuthContext'
import toast from 'react-hot-toast'
interface TestDetailViewProps {
  selectedExperiment: any
  onBack: () => void
}

interface ExperimentDetailResponse {
  experiment_id: number
  totals: {
    conversion_rate: number
    conversions: number
    revenue: number
    visits: number
  }
  variants: VariantDetail[]
  winner: {
    variant_name: string
    conversion_rate: number
    revenue: number
  }
}

interface VariantDetail {
  variant_name: string
  visits: number
  conversions: number
  conversion_rate: number
  revenue: number
  average_ticket: number
  traffic_percentage: number
  difference_vs_winner: number
}

export function TestDetailView({ selectedExperiment, onBack }: TestDetailViewProps) {
  const { language, country } = useAuth()
  const currencySymbol = getCurrencySymbol(country)
  const [data, setData] = useState<ExperimentDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const data = {
      owner_id: selectedExperiment.owner_id,
      website_id: selectedExperiment.website_id,
      experiment_id: selectedExperiment.id,
      experiment_variant_names: selectedExperiment.variants.map((variant: any) => variant.name)
    }
    getDetailExperimentForFunnel(data)
      .then((data) => {
        if (data?.length === 0) {
          setData(null)
        } else {
          setData(data)
        }
      })
      .catch((error) => {
        toast.error(dictionary['error-loading-experiment'][language])
        setData(null)
      })
      .finally(() => setLoading(false))
  }, [selectedExperiment])


  const formatNumber = (value: number) =>
    new Intl.NumberFormat('en-US').format(value)

  if (loading) {
    return <DetailLoadingSkeleton />
  }

  if (!data) {
    return <p className="text-sm text-red-500">{dictionary['error-loading-experiment'][language]}</p>
  }

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
      >
        <ArrowLeft className="w-4 h-4" />
        {dictionary['back'][language]}
      </button>

      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedExperiment.name}
            </h2>
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <span>{dictionary['funnel-id'][language]}: {selectedExperiment.website_id}</span>
              <span>|</span>
              <span className={`inline-flex items-center gap-1 ${selectedExperiment.status === 'active' ? 'text-green-700' : 'text-gray-500'}`}>
                <Play className="w-3 h-3 fill-current" />
                {selectedExperiment.status === 'active' ? dictionary['active'][language] : dictionary['paused'][language]}
              </span>
            </div>
          </div>

          <div className="flex gap-8">
            <Stat label={dictionary['visits'][language]} value={formatNumber(data.totals.visits)} />
            <Stat label={dictionary['conversions'][language]} value={formatNumber(data.totals.conversions)} />
            <Stat
              label={dictionary['conversion-rate'][language]}
              value={`${data.totals.conversion_rate.toFixed(2)}%`}
              highlight
            />
            <Stat
              label={dictionary['revenue'][language]}
              value={formatCurrency(data.totals.revenue, currencySymbol)}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{dictionary['variant-comparison'][language]}</h3>
          <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
            <Crown className="w-4 h-4" />
            {dictionary['winner'][language]}: {dictionary['variant'][language]} {data.winner.variant_name}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {data.variants.map((variant) => {
            const isWinner = variant.variant_name === data.winner.variant_name

            return (
              <div
                key={variant.variant_name}
                className={`rounded-xl p-5 bg-white ${isWinner
                  ? 'border-2 border-green-500 shadow-md'
                  : 'border border-gray-200'
                  }`}
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">
                      {dictionary['variant'][language]} {variant.variant_name}
                    </h4>
                    {isWinner && <Crown className="w-4 h-4 text-green-600" />}
                  </div>

                  {/* {!isWinner && (
                    <span className="flex items-center gap-1 text-sm text-red-500 font-medium">
                      <TrendingDown className="w-3 h-3" />
                      {variant.difference_vs_winner.toFixed(0)}%
                    </span>
                  )} */}
                </div>

                <p className="text-2xl font-bold mb-1">
                  {formatCurrency(variant.revenue, currencySymbol)}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  {variant.traffic_percentage}% {dictionary['traffic'][language]}
                </p>

                <div className="space-y-2 pt-4 border-t text-sm">
                  <Row label={dictionary['visits'][language]} value={formatNumber(variant.visits)} />
                  <Row label={dictionary['conversions'][language]} value={formatNumber(variant.conversions)} />
                  <Row
                    label={dictionary['conversion-rate'][language]}
                    value={`${variant.conversion_rate.toFixed(2)}%`}
                  />
                  <Row
                    label={dictionary['average-ticket'][language]}
                    value={formatCurrency(variant.average_ticket, currencySymbol)}
                  />
                  <Row
                    label={dictionary['revenue'][language]}
                    value={formatCurrency(variant.revenue, currencySymbol)}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function Stat({
  label,
  value,
  highlight
}: {
  label: string
  value: string
  highlight?: boolean
}) {
  return (
    <div className="text-center">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p
        className={`text-xl font-bold ${highlight ? 'text-green-600' : 'text-gray-900'
          }`}
      >
        {value}
      </p>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}:</span>
      <span className="font-medium text-gray-900">{value}</span>
    </div>
  )
}
